// @ts-nocheck
/**
 * TopBar Component
 * 
 * Application header with language selector,
 * cart button with badge, and settings button.
 * 
 * @author Heseda AI Team
 * @version 1.0
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { COMPACT_BP } from '../../hooks/useResponsive';
import { trackPageView } from '../../services/activityTrackingService';
import { INHOUSE_SELLING_ENABLED } from '../../config/adminConfig';
import { detectUserRegion, getRegionConfig } from '../../config/affiliateConfig';
import type { LanguageCode } from '../../types';

/** Display labels for supported UI languages */
const LANGUAGE_LABELS: Record<LanguageCode, string> = {
  en: 'EN',
  ko: 'KO',
  'pt-BR': 'PT',
  es: 'ES',
};

interface TopBarProps {
  language: LanguageCode;
  cartItemCount: number;
  wishlistCount: number;
  isCompact: boolean;
  /** User's profile country (from identity.country) */
  profileCountry?: string;
  /** Show a nudge pulse on the feedback icon to encourage submission */
  feedbackNudge?: boolean;
  /** Whether the wishlist/My Picks panel is currently open */
  isWishlistOpen?: boolean;
  onWishlistClick: () => void;
  onCartClick: () => void;
  onSettingsClick: () => void;
  onFeedbackClick?: () => void;
}

/**
 * Top bar with language selector, cart badge, and action buttons
 */
function TopBar({
  language,
  cartItemCount,
  wishlistCount,
  isCompact,
  profileCountry,
  feedbackNudge,
  isWishlistOpen,
  onWishlistClick,
  onCartClick,
  onSettingsClick,
  onFeedbackClick,
}: TopBarProps) {
  const { t } = useTranslation('common');

  // Resolve region & language for the indicator pill
  const region = detectUserRegion(profileCountry);
  const regionConfig = getRegionConfig(region);
  const uiLangLabel = LANGUAGE_LABELS[language] || language.toUpperCase();
  const regionLabel = language === 'ko' ? regionConfig.labelKo : regionConfig.label;
  // Short label for compact mobile: 2-letter country code (e.g. "US", "KR")
  const regionLabelShort = region;
  
  return (
    <div className="top-bar" data-testid="topbar">
      {/* Brand mark — left side */}
      <div className="topbar-brand">
        <img src="/logo-1024_simple.png" alt="" className="topbar-logo" width={28} height={28} />
        <span className="topbar-brand-name">Heseda</span>
      </div>

      <div className="topbar-actions">
        <button
          className="region-pill"
          data-testid="settings-region-pill"
          onClick={onSettingsClick}
          title={t('navigation.changeLanguage', { defaultValue: 'Change language & region' })}
          aria-label={t('navigation.changeLanguage', { defaultValue: 'Change language & region' })}
          type="button"
        >
          🌐 {isCompact ? regionLabelShort : regionLabel} · {uiLangLabel}
        </button>
        <button 
          onClick={() => {
            // User activity
            if (!isWishlistOpen) {
              trackPageView('wishlist');
            }

            onWishlistClick();
          }}
          className="settings-button"
          title={t('product.myPicks')}
          aria-label={wishlistCount > 0 ? `${t('product.myPicks')}, ${wishlistCount}` : t('product.myPicks')}
          aria-pressed={!!isWishlistOpen}
          aria-expanded={!!isWishlistOpen}
          aria-controls="wishlist-panel"
          style={{ position: 'relative' }}
        >
          <svg viewBox="0 0 24 24" width="20" height="20" fill={isWishlistOpen ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
          {wishlistCount > 0 && (
            <span className="topbar-badge topbar-badge--wishlist" aria-hidden="true">
              {wishlistCount}
            </span>
          )}
        </button>
        {INHOUSE_SELLING_ENABLED && (
        <button 
          onClick={() => {
            // User activity
            trackPageView('cart');
            
            onCartClick();
          }} 
          className="settings-button"
          title={t('navigation.shoppingCart')}
          aria-label={cartItemCount > 0 ? `${t('navigation.shoppingCart')}, ${cartItemCount}` : t('navigation.shoppingCart')}
          style={{ position: 'relative' }}
        >
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>
          {cartItemCount > 0 && (
            <span className="topbar-badge topbar-badge--cart" aria-hidden="true">
              {cartItemCount}
            </span>
          )}
        </button>
        )}
        {onFeedbackClick && (
        <button
          onClick={onFeedbackClick}
          className={`settings-button${feedbackNudge ? ' feedback-nudge' : ''}`}
          title={t('navigation.reportIssue')}
          aria-label={t('navigation.reportIssue')}
          data-testid="feedback-btn"
          style={{ position: 'relative' }}
        >
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
            <path d="M8 2l1.88 1.88M14.12 3.88L16 2M9 7.13v-1a3.003 3.003 0 116 0v1"/>
            <path d="M12 20c-3.3 0-6-2.7-6-6v-3a6 6 0 0112 0v3c0 3.3-2.7 6-6 6z"/>
            <path d="M12 20v2M6 11H4M20 11h-2"/>
          </svg>
          {feedbackNudge && (
            <span
              aria-hidden="true"
              style={{
                position: 'absolute',
                top: '-2px',
                right: '-2px',
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: '#f59e0b',
                animation: 'feedback-pulse 2s ease-in-out infinite',
              }}
            />
          )}
        </button>
        )}
        <button 
          onClick={() => {
            // User activity
            trackPageView('settings');

            onSettingsClick();
          }} 
          className="settings-button"
          title={t('navigation.settings')}
          data-testid="settings-btn"
        >
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
        </button>
      </div>
    </div>
  );
}

export default TopBar;
